import { api } from "@/lib/api";
import {
  AgencyDto,
  agencyDtoListSchema,
  AgencyDtoSchema,
  agencyFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getAgenciesQueryOptions = queryOptions({
  queryKey: ["agencies"],
  queryFn: async () => {
    const result: Array<AgencyDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/agencies", agencyDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(agencyFromDto);
  },
});

export function useGetAgencies<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getAgenciesQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getAgenciesError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getAgencyQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["agencies", id],
    queryFn: () =>
      api
        .get("/api/agencies/$id", AgencyDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(agencyFromDto),
  });
}

export function useGetAgency<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void },
) {
  const { toast } = useToast();
  const q = useQuery(getAgencyQueryOptions(id));
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getAgencyDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
